/**
 * Client-only AccunityHR product tour — single file.
 * Explain each module when its step arrives (not all up front).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./ClientLiveTour.css";

const TOUR_STORAGE_KEY = "accunityhr.clientLiveTour.v5";
const ORG = "AccunityHR";
const ORG_LABEL = "ACC01 - AccunityHR";
const BRANCH = "AccunityHR Bangalore";
const BRANCH_LABEL = "BNG01 - AccunityHR Bangalore";
const YEAR = "2026";
const MONTH = "Aug";
const REPORT_MONTH = "May";
const REPORT_MONTH_LABEL = "May 2026";

const TOUR_CHECKLIST = [
  { id: "welcome", label: "Welcome" },
  { id: "nav", label: "Dashboard" },
  { id: "forms", label: "Forms" },
  { id: "reports", label: "Reports" },
  { id: "returns", label: "Returns" },
  { id: "docs", label: "Documents" },
  { id: "done", label: "Finish" },
];

const LIVE_TOUR_STEPS = [
  {
    id: "welcome",
    module: "welcome",
    kind: "center",
    title: "Welcome to AccunityHR",
    body: "A short tour of your Client portal. Each area is explained when you reach it. Demo data uses AccunityHR · AccunityHR Bangalore.",
    cta: "Continue",
  },
  {
    id: "nav-dashboard",
    module: "nav",
    kind: "spotlight",
    route: "/dashboard",
    target: "nav-dashboard",
    placement: "right",
    title: "Dashboard",
    body: "Your home base — open it anytime from the sidebar.",
    arrow: true,
  },
  {
    id: "dashboard-kpis",
    module: "nav",
    kind: "spotlight",
    route: "/dashboard",
    target: "dashboard-kpis",
    placement: "bottom",
    title: "Summary cards",
    body: "Organization, Branches, Employee Head Count, New Employees and Exit Employees — a quick snapshot of your account.",
    arrow: true,
  },
  {
    id: "dashboard-registers",
    module: "nav",
    kind: "spotlight",
    route: "/dashboard",
    target: "dashboard-registers",
    placement: "bottom",
    title: "Registers Overview",
    body: "See how many forms were generated across months for the selected financial year.",
    arrow: true,
  },
  {
    id: "dashboard-status",
    module: "nav",
    kind: "spotlight",
    route: "/dashboard",
    target: "dashboard-status",
    placement: "bottom",
    title: "Status Chart",
    body: "Track completed vs pending forms at a glance.",
    arrow: true,
  },
  {
    id: "dashboard-returns",
    module: "nav",
    kind: "spotlight",
    route: "/dashboard",
    target: "dashboard-returns",
    placement: "bottom",
    title: "Returns",
    body: "Upcoming and recent statutory returns with due dates.",
    arrow: true,
  },
  {
    id: "dashboard-holidays",
    module: "nav",
    kind: "spotlight",
    route: "/dashboard",
    target: "dashboard-holidays",
    placement: "top",
    title: "Upcoming Holidays",
    body: "Festival and national holidays for planning — filter by type if needed.",
    arrow: true,
  },
  {
    id: "nav-forms",
    module: "forms",
    kind: "spotlight",
    route: "/generated-form",
    target: "nav-forms",
    placement: "right",
    openRegister: true,
    title: "Register · Forms",
    body: "Statutory forms your AccunityHR provider generates. You view and download them here.",
    arrow: true,
  },
  {
    id: "forms-demo",
    module: "forms",
    kind: "demo",
    demo: "forms",
    route: "/generated-form",
    title: "Forms",
    body: "Follow the highlight: filters → Branch → Year → Month → View forms.",
    itemKind: "forms",
  },
  {
    id: "nav-reports",
    module: "reports",
    kind: "spotlight",
    route: "/report",
    target: "nav-reports",
    placement: "right",
    openRegister: true,
    title: "Register · Reports",
    body: "Payroll reports by organization and branch — same hub as Register → Reports.",
    arrow: true,
  },
  {
    id: "reports-demo",
    module: "reports",
    kind: "demo",
    demo: "reports",
    route: "/report",
    title: "Reports",
    body: "Follow the hub: Organization, Branch, search, then open a report and use Year / Month.",
  },
  {
    id: "nav-returns",
    module: "returns",
    kind: "spotlight",
    route: "/returns",
    target: "nav-returns",
    placement: "right",
    title: "Returns",
    body: "Statutory filings such as PF Return — same Branch → Year → Month → View pattern.",
    arrow: true,
  },
  {
    id: "returns-demo",
    module: "returns",
    kind: "demo",
    demo: "returns",
    route: "/returns",
    title: "Returns",
    body: "Follow the highlight through filters, view, download and bulk.",
    itemKind: "returns",
  },
  {
    id: "nav-docs",
    module: "docs",
    kind: "spotlight",
    route: "/compliance-documents",
    target: "nav-docs",
    placement: "right",
    title: "Compliance Documents",
    body: "Licences and challans uploaded by your provider — you only view and download.",
    arrow: true,
  },
  {
    id: "docs-demo",
    module: "docs",
    kind: "demo",
    demo: "docs",
    route: "/compliance-documents",
    title: "Documents",
    body: "Follow the highlight: filters → Branch → Year → Month → View documents.",
    itemKind: "docs",
  },
  {
    id: "done",
    module: "done",
    kind: "center",
    route: "/user-guide",
    title: "You're ready",
    body: "Dashboard → Forms → Reports → Returns → Documents. Use the same steps with live files when your provider generates them.",
    cta: "Finish",
  },
];

/** Coach copy while a list demo is mid-flow */
function demoCoachCopy(itemKind, phase) {
  const noun = itemKind === "docs" ? "documents" : itemKind === "returns" ? "returns" : "forms";
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  const map = {
    overview: {
      title: Noun,
      body: "Organization is fixed as AccunityHR. Branch, Year, Month and View live in this filter strip. Click Next to continue.",
    },
    branch: { title: Noun, body: "Select Branch → AccunityHR Bangalore." },
    year: { title: Noun, body: "Select Year → 2026." },
    month: { title: Noun, body: "Select Month → Aug." },
    view: { title: Noun, body: `Click View ${noun === "forms" ? "forms" : "documents"} to load the list.` },
    list: {
      title: `${Noun} list`,
      body: `${Noun} appear in this list. You can view and download them here. Click Next to continue.`,
    },
    eye: { title: `View ${noun}`, body: "Click the eye icon to open a preview." },
    preview_close: { title: `View ${noun}`, body: "Click Close on the preview to continue." },
    single: { title: "Download", body: "Click the download icon. A dialog shows how download works." },
    download_close: { title: "Download", body: "Click Close on the download dialog to continue." },
    check: { title: "Bulk download", body: "First select the checkbox on the row." },
    bulk: { title: "Bulk download", body: "Now click Bulk to download the selected files." },
    bulk_close: { title: "Bulk download", body: "Click Close on the download dialog to finish." },
    done: { title: Noun, body: "Section complete — click Next." },
  };
  return map[phase] || { title: Noun, body: "Follow the highlighted control." };
}

function reportCoachCopy(phase) {
  const map = {
    overview: {
      title: "Reports hub",
      body: "This is Register → Reports. Current month shows the payroll period. Organization is locked. Click Next.",
    },
    branch: {
      title: "Branch",
      body: `Select Branch → ${BRANCH_LABEL}. Cards stay locked until a branch is chosen.`,
    },
    search: {
      title: "Search reports",
      body: "Search reports by name, code, or category. Available reports shows how many you can open. Click Next.",
    },
    cards: {
      title: "MONTHLY INPUTS",
      body: "Reports are grouped by category. Open Employee Master or Salary Register.",
    },
    list: {
      title: "Report table",
      body: "The report opens for the current month. Year and Month sit in the table header — same as live Reports. Click Next.",
    },
    year: {
      title: "Payroll year",
      body: "Use Year to pick the payroll year. Choose 2026.",
    },
    month: {
      title: "Payroll month",
      body: `Use Month to pick the payroll month. Choose ${REPORT_MONTH}.`,
    },
    filter: {
      title: "Search rows",
      body: 'Search an employee in this report — try "Asha".',
    },
    rows: { title: "Reports", body: "Filtered results are ready — click Next." },
    done: { title: "Reports", body: "Section complete — click Next." },
  };
  return map[phase] || { title: "Reports", body: "Follow the highlighted control." };
}

const EMP_ROWS = [
  { emp_code: "E001", emp_name: "Asha Rao", unit_id: "BNG01", dept: "Marketing", status: "Active" },
  { emp_code: "E002", emp_name: "Vikram Shah", unit_id: "BNG01", dept: "Marketing", status: "Active" },
  { emp_code: "E003", emp_name: "Neha Patel", unit_id: "BNG01", dept: "Marketing", status: "Active" },
  { emp_code: "E004", emp_name: "Rohan Mehta", unit_id: "BNG01", dept: "Marketing", status: "Active" },
  { emp_code: "E005", emp_name: "Priya Nair", unit_id: "BNG01", dept: "Marketing", status: "Active" },
];

const SALARY_ROWS = [
  { emp_code: "E001", emp_name: "Asha Rao", unit_id: "BNG01", basic: "45,000", allow: "8,000", net: "48,200" },
  { emp_code: "E002", emp_name: "Vikram Shah", unit_id: "BNG01", basic: "32,000", allow: "5,500", net: "34,100" },
  { emp_code: "E003", emp_name: "Neha Patel", unit_id: "BNG01", basic: "28,000", allow: "4,200", net: "29,400" },
  { emp_code: "E004", emp_name: "Rohan Mehta", unit_id: "BNG01", basic: "30,000", allow: "4,800", net: "31,600" },
  { emp_code: "E005", emp_name: "Priya Nair", unit_id: "BNG01", basic: "26,000", allow: "3,900", net: "27,200" },
];

const REPORT_HUB_CARDS = [
  {
    kind: "employee",
    id: "employee-master",
    title: "Employee Master",
    description: "All active employees by default. Use month to see who was on payroll or joined that month.",
  },
  {
    kind: "salary",
    id: "salary-register",
    title: "Salary Register",
    description: "Earnings, deductions, and net pay by employee for the month.",
  },
  {
    kind: "ctc",
    id: "ctc-master",
    title: "CTC Master",
    description: "Cost-to-company structure and components by employee.",
  },
];

const ClientTourContext = createContext(null);

function loadState() {
  try {
    const raw = localStorage.getItem(TOUR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveState(partial) {
  try {
    const prev = loadState() || {};
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify({ ...prev, ...partial }));
  } catch {
    /* ignore */
  }
}

function getTargetRect(target) {
  if (!target) return null;
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 && rect.height < 2) return null;
  return { el, rect };
}

function DemoSpotlight({ targetKey, tip, placement = "bottom" }) {
  const [hole, setHole] = useState(null);
  const elevated = targetKey === "preview-close" || targetKey === "dl-close";

  useLayoutEffect(() => {
    if (!targetKey) {
      setHole(null);
      return undefined;
    }
    const measure = () => {
      const el = document.querySelector(`[data-ct-local="${targetKey}"]`);
      if (!el) {
        setHole(null);
        return;
      }
      const r = el.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) {
        setHole(null);
        return;
      }
      const pad = 10;
      setHole({
        top: Math.max(0, r.top - pad),
        left: Math.max(0, r.left - pad),
        width: r.width + pad * 2,
        height: r.height + pad * 2,
        cx: r.left + r.width / 2,
        cy: r.top + r.height / 2,
        bottom: r.bottom + pad,
        right: r.right + pad,
      });
    };
    measure();
    const id = window.setInterval(measure, 120);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [targetKey]);

  if (!hole || !targetKey) return null;

  const tipStyle =
    placement === "right"
      ? { top: Math.min(hole.top, window.innerHeight - 100), left: hole.right + 16 }
      : placement === "top"
      ? {
          top: Math.max(72, hole.top - 72),
          left: Math.min(Math.max(12, hole.cx - 140), window.innerWidth - 300),
        }
      : {
          top: Math.min(hole.bottom + 14, window.innerHeight - 90),
          left: Math.min(Math.max(12, hole.cx - 140), window.innerWidth - 300),
        };

  const arrowStyle =
    placement === "right"
      ? { top: hole.cy - 10, left: hole.right + 2 }
      : placement === "top"
      ? { top: hole.top - 18, left: hole.cx - 10 }
      : { top: hole.bottom + 2, left: hole.cx - 10 };

  const node = (
    <div className={"ct-demo-spot" + (elevated ? " is-modal" : "")} aria-hidden="true">
      <div className="ct-local-dim" style={{ top: 0, left: 0, right: 0, height: hole.top }} />
      <div
        className="ct-local-dim"
        style={{ top: hole.top, left: 0, width: hole.left, height: hole.height }}
      />
      <div
        className="ct-local-dim"
        style={{
          top: hole.top,
          left: hole.left + hole.width,
          right: 0,
          height: hole.height,
        }}
      />
      <div
        className="ct-local-dim"
        style={{ top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }}
      />
      <div
        className="ct-local-ring"
        style={{
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
        }}
      />
      <div
        className={
          "ct-local-arrow " + (placement === "right" ? "right" : placement === "top" ? "up" : "down")
        }
        style={arrowStyle}
      />
      {tip ? (
        <div className="ct-local-tip" style={tipStyle}>
          {tip}
        </div>
      ) : null}
    </div>
  );

  const root = typeof document !== "undefined" ? document.querySelector(".ct-root") : null;
  return root ? createPortal(node, root) : node;
}

const LIST_PHASE_SPOT = {
  overview: {
    key: "filters",
    tip: "Org is fixed · Branch, Year, Month & View are here",
    placement: "bottom",
  },
  branch: { key: "branch", tip: "Choose AccunityHR Bangalore", placement: "bottom" },
  year: { key: "year", tip: "Choose 2026", placement: "bottom" },
  month: { key: "month", tip: "Choose Aug", placement: "bottom" },
  view: { key: "view", tip: "Click to load the list", placement: "bottom" },
  list: { key: "list", tip: "Your files appear here — click Next", placement: "top" },
  eye: { key: "eye", tip: "Click View to open", placement: "bottom" },
  preview_close: { key: "preview-close", tip: "Click Close to continue", placement: "bottom" },
  single: { key: "single", tip: "Click to download", placement: "bottom" },
  download_close: { key: "dl-close", tip: "Click Close to continue", placement: "bottom" },
  check: { key: "check", tip: "Select this row first", placement: "right" },
  bulk: { key: "bulk", tip: "Click Bulk download", placement: "bottom" },
  bulk_close: { key: "dl-close", tip: "Click Close to finish", placement: "bottom" },
};

const REPORT_PHASE_SPOT = {
  overview: {
    key: "r-hero",
    tip: "Reports · Current month · Organization · Branch",
    placement: "bottom",
  },
  branch: { key: "r-branch", tip: `Choose ${BRANCH_LABEL}`, placement: "bottom" },
  search: {
    key: "r-search",
    tip: "Search by name, code, or category",
    placement: "bottom",
  },
  cards: { key: "r-cards", tip: "Open Employee Master or Salary Register", placement: "bottom" },
  list: { key: "r-table", tip: "Report rows + Year / Month in the header", placement: "top" },
  year: { key: "r-year", tip: "Choose Year → 2026", placement: "bottom" },
  month: { key: "r-month", tip: `Choose Month → ${REPORT_MONTH}`, placement: "bottom" },
  filter: { key: "r-row-search", tip: 'Search e.g. "Asha"', placement: "bottom" },
  rows: { key: "r-table", tip: "Filtered results — click Next", placement: "top" },
};

/** Shared filter+list demo matching real client Forms / Returns / Docs UI */
function RegisterListDemo({
  pageTitle,
  pageHint,
  viewLabel,
  rowName,
  itemKind = "forms",
  phase,
  setPhase,
  onToast,
  onPreview,
  onDownloadInfo,
}) {
  const [branch, setBranch] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [listed, setListed] = useState(false);
  const [checked, setChecked] = useState(false);

  const spot =
    phase === "preview_close" || phase === "download_close" || phase === "bulk_close"
      ? null
      : LIST_PHASE_SPOT[phase];
  const live = (key) => (phase === key ? " ct-live" : "");
  const noun = itemKind === "docs" ? "documents" : itemKind === "returns" ? "returns" : "forms";

  return (
    <div className="ct-page" data-tour="tour-demo-stage">
      {spot ? (
        <DemoSpotlight targetKey={spot.key} tip={spot.tip} placement={spot.placement} />
      ) : null}

      <div className="ct-page-head">
        <h1>{pageTitle}</h1>
        <span className="ct-badge">Demo · not live data</span>
      </div>
      <p className="ct-page-lead">{pageHint}</p>

      <div className={"ct-filters-strip" + live("overview")} data-ct-local="filters">
        <div className="ct-org-card">
          <div className="ct-org-icon">A</div>
          <div>
            <span>Organization</span>
            <strong>{ORG}</strong>
            <em>Organisation stays fixed while you are logged in as Client.</em>
          </div>
        </div>

        <div className="ct-filter-row">
          <div className={"ct-field" + live("branch")} data-ct-local="branch">
            <label>Branch</label>
            <select
              value={branch}
              disabled={phase !== "branch"}
              onChange={(e) => {
                if (phase !== "branch") return;
                setBranch(e.target.value);
                setPhase("year");
              }}
            >
              <option value="">Select branch</option>
              <option value={BRANCH}>{BRANCH}</option>
            </select>
          </div>
          <div className={"ct-field" + live("year")} data-ct-local="year">
            <label>Year</label>
            <select
              value={year}
              disabled={phase !== "year"}
              onChange={(e) => {
                if (phase !== "year") return;
                setYear(e.target.value);
                setPhase("month");
              }}
            >
              <option value="">Select year</option>
              <option value={YEAR}>{YEAR}</option>
            </select>
          </div>
          <div className={"ct-field" + live("month")} data-ct-local="month">
            <label>Month</label>
            <select
              value={month}
              disabled={phase !== "month"}
              onChange={(e) => {
                if (phase !== "month") return;
                setMonth(e.target.value);
                setPhase("view");
              }}
            >
              <option value="">Select month</option>
              <option value={MONTH}>{MONTH}</option>
            </select>
          </div>
          <button
            type="button"
            className={"ct-submit" + live("view")}
            data-ct-local="view"
            disabled={phase !== "view"}
            onClick={() => {
              if (phase !== "view") return;
              setListed(true);
              setPhase("list");
              onToast(`${ORG} · ${BRANCH} · ${MONTH} ${YEAR}`);
            }}
          >
            {viewLabel}
          </button>
        </div>
      </div>

      {listed ? (
        <div className={"ct-table-wrap" + live("list")} data-ct-local="list">
          <div className="ct-table-toolbar">
            <strong>
              {BRANCH} · {MONTH} {YEAR}
            </strong>
            <button
              type="button"
              className={"ct-bulk-btn" + live("bulk")}
              data-ct-local="bulk"
              disabled={phase !== "bulk" && phase !== "done"}
              onClick={() => {
                if (phase !== "bulk") return;
                if (!checked) {
                  onToast("Select the checkbox first");
                  setPhase("check");
                  return;
                }
                onDownloadInfo?.({
                  title: "Bulk download",
                  text: "Selected files download together as a zip.",
                });
                setPhase("bulk_close");
              }}
            >
              ⬇ Bulk
            </button>
          </div>
          <table className="ct-table">
            <thead>
              <tr>
                <th />
                <th>Name</th>
                <th>Location</th>
                <th>Month</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <label className={"ct-check-wrap" + live("check")} data-ct-local="check">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={phase !== "check" && phase !== "bulk" && phase !== "done"}
                      onChange={(e) => {
                        if (phase !== "check" && phase !== "bulk") return;
                        setChecked(e.target.checked);
                        if (e.target.checked) setPhase("bulk");
                      }}
                    />
                  </label>
                </td>
                <td>{rowName}</td>
                <td>{BRANCH}</td>
                <td>
                  {MONTH} {YEAR}
                </td>
                <td>
                  <div className="ct-row-acts">
                    <button
                      type="button"
                      title="View"
                      className={live("eye")}
                      data-ct-local="eye"
                      disabled={
                        phase !== "eye" &&
                        phase !== "preview_close" &&
                        phase !== "single" &&
                        phase !== "download_close" &&
                        phase !== "check" &&
                        phase !== "bulk" &&
                        phase !== "bulk_close" &&
                        phase !== "done"
                      }
                      onClick={() => {
                        if (phase !== "eye") return;
                        onPreview({
                          title: rowName,
                          text: `${noun.charAt(0).toUpperCase() + noun.slice(1)} can be viewed here.`,
                        });
                        setPhase("preview_close");
                      }}
                    >
                      👁
                    </button>
                    <button
                      type="button"
                      title="Download"
                      className={live("single")}
                      data-ct-local="single"
                      disabled={
                        phase !== "single" &&
                        phase !== "download_close" &&
                        phase !== "check" &&
                        phase !== "bulk" &&
                        phase !== "bulk_close" &&
                        phase !== "done"
                      }
                      onClick={() => {
                        if (phase !== "single") return;
                        onDownloadInfo?.({
                          title: "Download",
                          text: "The file will download here.",
                        });
                        setPhase("download_close");
                      }}
                    >
                      ⬇
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="ct-legend">
            <b>👁 View</b> · <b>⬇</b> one file · <b>Bulk</b> selected rows
          </div>
          {phase === "done" ? <div className="ct-done-strip">✓ Done — click Next</div> : null}
        </div>
      ) : (
        <div className="ct-placeholder">
          <div className="ct-docs-illus" aria-hidden="true">
            <div />
            <div className="is-on" />
            <div />
          </div>
          <p>Items appear here after you click {viewLabel}.</p>
        </div>
      )}
    </div>
  );
}

function ReportsDemo({ phase, setPhase, onToast }) {
  const [branch, setBranch] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [report, setReport] = useState(null);
  const [hubQuery, setHubQuery] = useState("");
  const [filter, setFilter] = useState("");
  const showTable = Boolean(report) && ["list", "year", "month", "filter", "rows", "done"].includes(phase);
  const rows = report === "salary" ? SALARY_ROWS : EMP_ROWS;
  const filtered = rows.filter((r) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return Object.values(r).some((v) => String(v).toLowerCase().includes(q));
  });
  const hubCards = REPORT_HUB_CARDS.filter((card) => {
    const q = hubQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      card.title.toLowerCase().includes(q) ||
      card.id.toLowerCase().includes(q) ||
      card.description.toLowerCase().includes(q) ||
      "monthly inputs".includes(q)
    );
  });
  const spot = REPORT_PHASE_SPOT[phase];
  const live = (key) => (phase === key ? " ct-live" : "");
  const branchReady = Boolean(branch);
  const cardsLocked = !branchReady;

  const openReport = (kind, title) => {
    if (phase !== "cards" && phase !== "list" && phase !== "filter" && phase !== "rows") return;
    if (kind === "ctc") return;
    setReport(kind);
    setYear("");
    setMonth("");
    setFilter("");
    setPhase("list");
    onToast(`${title} — AccunityHR Bangalore · ${REPORT_MONTH_LABEL}`);
  };

  return (
    <div className="ct-page ct-reports-page" data-tour="tour-demo-stage">
      {spot ? <DemoSpotlight targetKey={spot.key} tip={spot.tip} placement={spot.placement} /> : null}

      {showTable ? (
        <div className={"ct-report-view" + (phase === "list" || phase === "rows" ? " ct-live" : "")} data-ct-local="r-table">
          <div className="ct-report-crumb">
            Reports <span>/</span> {report === "salary" ? "Salary Register" : "Employee Master"}
          </div>
          <div className="ct-report-head">
            <div>
              <h1>{report === "salary" ? "Salary Register" : "Employee Master"}</h1>
              <p>
                {BRANCH_LABEL} · {year && month ? `${month} ${year}` : REPORT_MONTH_LABEL}
              </p>
            </div>
            <span className="ct-badge">Demo · not live data</span>
          </div>
          <div className="ct-table-toolbar ct-report-toolbar">
            <input
              className={"ct-search" + live("filter")}
              data-ct-local="r-row-search"
              placeholder="Search here..."
              value={filter}
              disabled={phase !== "filter" && phase !== "rows" && phase !== "done"}
              onChange={(e) => {
                if (phase !== "filter" && phase !== "rows") return;
                const v = e.target.value;
                setFilter(v);
                const q = v.toLowerCase().trim();
                if (
                  q &&
                  rows.some((r) => Object.values(r).some((val) => String(val).toLowerCase().includes(q)))
                ) {
                  setPhase("rows");
                }
              }}
            />
            <select
              className={"ct-period" + live("year")}
              data-ct-local="r-year"
              value={year}
              disabled={phase !== "year"}
              aria-label="Payroll year"
              onChange={(e) => {
                if (phase !== "year") return;
                setYear(e.target.value);
                setMonth("");
                if (e.target.value === YEAR) setPhase("month");
              }}
            >
              <option value="">Year</option>
              <option value={YEAR}>{YEAR}</option>
            </select>
            <select
              className={"ct-period" + live("month")}
              data-ct-local="r-month"
              value={month}
              disabled={phase !== "month" || !year}
              aria-label="Payroll month"
              onChange={(e) => {
                if (phase !== "month") return;
                setMonth(e.target.value);
                if (e.target.value === REPORT_MONTH) setPhase("filter");
              }}
            >
              <option value="">Month</option>
              <option value={REPORT_MONTH}>{REPORT_MONTH}</option>
            </select>
            <span className="ct-report-entries">Total Entries : {filtered.length}</span>
          </div>
          <table className="ct-table">
            <thead>
              {report === "salary" ? (
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Branch ID</th>
                  <th>Basic</th>
                  <th>Allowances</th>
                  <th>Net</th>
                </tr>
              ) : (
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Branch ID</th>
                  <th>Department</th>
                  <th>Status</th>
                </tr>
              )}
            </thead>
            <tbody>
              {filtered.map((r) =>
                report === "salary" ? (
                  <tr key={r.emp_code}>
                    <td>{r.emp_code}</td>
                    <td>{r.emp_name}</td>
                    <td>{r.unit_id}</td>
                    <td>{r.basic}</td>
                    <td>{r.allow}</td>
                    <td>{r.net}</td>
                  </tr>
                ) : (
                  <tr key={r.emp_code}>
                    <td>{r.emp_code}</td>
                    <td>{r.emp_name}</td>
                    <td>{r.unit_id}</td>
                    <td>{r.dept}</td>
                    <td>{r.status}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
          {(phase === "rows" || phase === "done") && (
            <div className="ct-done-strip">✓ Reports demo ready — click Next</div>
          )}
        </div>
      ) : (
        <>
          <div className={"ct-rhub-hero" + live("overview")} data-ct-local="r-hero">
            <div className="ct-rhub-head">
              <h1>Reports</h1>
              <div className="ct-rhub-month">
                <span className="ct-rhub-live">Current month</span>
                <span className="ct-rhub-pill">{REPORT_MONTH_LABEL}</span>
              </div>
            </div>
            <div className="ct-rhub-scope">
              <label className="ct-rhub-field">
                <span>Organization</span>
                <select value={ORG} disabled aria-label="Organization">
                  <option value={ORG}>{ORG_LABEL}</option>
                </select>
              </label>
              <label className={"ct-rhub-field" + live("branch")} data-ct-local="r-branch">
                <span>Branch</span>
                <select
                  value={branch}
                  disabled={phase !== "branch"}
                  aria-label="Branch"
                  onChange={(e) => {
                    if (phase !== "branch") return;
                    setBranch(e.target.value);
                    setPhase("search");
                  }}
                >
                  <option value="">Select branch</option>
                  <option value={BRANCH}>{BRANCH_LABEL}</option>
                </select>
              </label>
            </div>
            <div className={"ct-rhub-toolbar" + live("search")} data-ct-local="r-search">
              <input
                type="search"
                placeholder="Search reports by name, code, or category..."
                value={hubQuery}
                disabled={cardsLocked || (phase !== "search" && phase !== "cards")}
                onChange={(e) => setHubQuery(e.target.value)}
              />
              <span className="ct-rhub-count">
                <em>Available reports</em>
                <b>{hubQuery.trim() ? hubCards.length : 17}</b>
              </span>
            </div>
          </div>

          <div
            className={
              "ct-rhub-section" +
              live("cards") +
              (cardsLocked ? " is-locked" : "")
            }
            data-ct-local="r-cards"
          >
            <div className="ct-rhub-section-head">
              <div className="ct-rhub-section-icon" aria-hidden="true" />
              <div>
                <h2>MONTHLY INPUTS</h2>
                <p>Payroll registers, attendance, leave, and monthly employee inputs.</p>
              </div>
              <span className="ct-rhub-section-count">13 reports</span>
            </div>
            <div className="ct-rhub-grid">
              {hubCards.map((card) => {
                const clickable = card.kind !== "ctc";
                return (
                  <button
                    key={card.id}
                    type="button"
                    className={"ct-rhub-card" + (report === card.kind ? " is-on" : "")}
                    disabled={cardsLocked || !clickable}
                    onClick={() => openReport(card.kind, card.title)}
                  >
                    <div>
                      <b>{card.title}</b>
                      <p>{card.description}</p>
                    </div>
                    <span className="ct-rhub-slug">{card.id}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {cardsLocked ? (
            <p className="ct-rhub-hint">Select AccunityHR Bangalore to unlock report cards.</p>
          ) : null}
        </>
      )}
    </div>
  );
}

function PreviewModal({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="ct-preview-layer" role="dialog" aria-modal="true">
      <div className="ct-preview-card ct-preview-card-simple">
        <div className="ct-preview-top">
          <div>
            <span className="ct-badge">Preview</span>
            <h3>{item.title}</h3>
          </div>
          <button
            type="button"
            className="ct-x"
            data-ct-local="preview-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="ct-preview-sheet ct-preview-sheet-simple">
          <p className="ct-sheet-copy">{item.text || "Forms can be viewed here."}</p>
          <p className="ct-sheet-copy" style={{ color: "#0057ff", fontWeight: 600 }}>
            Click Close to continue.
          </p>
        </div>
        <div className="ct-preview-bot">
          <span />
          <button type="button" className="ct-btn" data-ct-local="preview-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DownloadDialog({ item, onClose }) {
  if (!item) return null;

  const handleClose = () => {
    const then = item.then;
    onClose();
    if (typeof then === "function") then();
  };

  return (
    <div className="ct-preview-layer" role="dialog" aria-modal="true">
      <div className="ct-preview-card ct-preview-card-simple ct-download-card">
        <div className="ct-preview-top">
          <div>
            <span className="ct-badge">Download</span>
            <h3>{item.title || "Download"}</h3>
          </div>
          <button
            type="button"
            className="ct-x"
            data-ct-local="dl-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="ct-preview-sheet ct-preview-sheet-simple">
          <p className="ct-sheet-copy">{item.text || "The file will download here."}</p>
          <p className="ct-sheet-copy" style={{ color: "#0057ff", fontWeight: 600 }}>
            Click Close to continue.
          </p>
        </div>
        <div className="ct-preview-bot">
          <span />
          <button type="button" className="ct-btn" data-ct-local="dl-close" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Arrow({ rect, placement }) {
  if (!rect) return null;
  const style =
    placement === "right"
      ? { top: rect.top + rect.height / 2 - 14, left: rect.right + 8 }
      : placement === "top"
      ? { top: rect.top - 36, left: rect.left + rect.width / 2 - 14 }
      : { top: rect.bottom + 6, left: rect.left + rect.width / 2 - 14 };
  const cls =
    placement === "right" ? "ct-arrow right" : placement === "top" ? "ct-arrow up" : "ct-arrow down";
  return <div className={cls} style={style} aria-hidden="true" />;
}

function TipBubble({ rect, text, placement = "bottom" }) {
  if (!rect || !text) return null;
  const cx = rect.left + rect.width / 2;
  const style =
    placement === "right"
      ? {
          top: Math.min(Math.max(72, rect.top), window.innerHeight - 110),
          left: Math.min(rect.right + 28, window.innerWidth - 300),
        }
      : placement === "top"
      ? {
          top: Math.max(72, rect.top - 78),
          left: Math.min(Math.max(12, cx - 140), window.innerWidth - 300),
        }
      : {
          top: Math.min(rect.bottom + 28, window.innerHeight - 110),
          left: Math.min(Math.max(12, cx - 140), window.innerWidth - 300),
        };
  return (
    <div className="ct-tip-bubble" style={style} role="note">
      {text}
    </div>
  );
}

function CoachBar({ step, index, total, isFirst, isLast, next, back, skip, nextDisabled }) {
  return (
    <header className="ct-coach-bar" role="dialog" aria-label={step.title}>
      <div className="ct-coach-bar-text">
        <span className="ct-step">
          {index + 1}/{total}
        </span>
        <strong>{step.title}</strong>
        <p>{step.body}</p>
      </div>
      <div className="ct-coach-bar-actions">
        <button type="button" className="ghost" onClick={skip}>
          Skip
        </button>
        <button type="button" className="ghost" disabled={isFirst} onClick={back}>
          Back
        </button>
        <button type="button" className="primary" disabled={nextDisabled} onClick={next}>
          {isLast ? "Finish" : step.cta || "Next"}
        </button>
      </div>
    </header>
  );
}

function ClientTourOverlay() {
  const {
    step,
    index,
    total,
    isFirst,
    isLast,
    next,
    back,
    skip,
    checklistItems,
    checklistOpen,
    setChecklistOpen,
    jumpModule,
    stopTour,
    demoPhase,
    setDemoPhase,
  } = useClientTour();

  const [rect, setRect] = useState(null);
  const [preview, setPreview] = useState(null);
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [toast, setToast] = useState("");
  const [viewport, setViewport] = useState({
    w: typeof window !== "undefined" ? window.innerWidth : 1200,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  });

  const showToast = (msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2400);
  };

  const measure = useCallback(() => {
    setViewport({ w: window.innerWidth, h: window.innerHeight });
    if (!step || step.kind !== "spotlight" || !step.target) {
      setRect(null);
      return;
    }
    const hit = getTargetRect(step.target);
    if (!hit) {
      setRect(null);
      return;
    }
    setRect(hit.rect);
  }, [step]);

  useLayoutEffect(() => {
    let n = 0;
    let timer;
    const tick = () => {
      measure();
      n += 1;
      if (n < 14) timer = window.setTimeout(tick, 180);
    };
    timer = window.setTimeout(tick, 50);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [measure, index]);

  useEffect(() => {
    setPreview(null);
    setDownloadInfo(null);
    if (step?.kind === "demo") setDemoPhase("overview");
  }, [step?.id, setDemoPhase]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const blockKeys = (e) => {
      if (e.key === "Tab") e.preventDefault();
    };
    window.addEventListener("keydown", blockKeys, true);

    if (step?.kind === "spotlight" && step.target) {
      document.body.style.overflow = "auto";
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      window.setTimeout(() => {
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
        measure();
      }, 80);
    } else {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", blockKeys, true);
    };
  }, [step?.id, step?.kind, step?.target, measure]);

  const handleNext = useCallback(() => {
    if (step?.kind === "demo") {
      if (demoPhase === "overview") {
        setDemoPhase("branch");
        return;
      }
      if (step.demo === "reports" && demoPhase === "search") {
        setDemoPhase("cards");
        return;
      }
      if (demoPhase === "list") {
        setDemoPhase(step.demo === "reports" ? "year" : "eye");
        return;
      }
      if (preview && demoPhase === "eye") {
        setDemoPhase("preview_close");
        return;
      }
      if (downloadInfo && demoPhase === "single") {
        setDemoPhase("download_close");
        return;
      }
      if (downloadInfo && demoPhase === "bulk") {
        setDemoPhase("bulk_close");
        return;
      }
    }
    next();
  }, [step, demoPhase, preview, downloadInfo, setDemoPhase, next]);

  const handlePreviewClose = useCallback(() => {
    setPreview(null);
    setDemoPhase("single");
  }, [setDemoPhase]);

  const handleDownloadClose = useCallback(() => {
    setDownloadInfo(null);
    setDemoPhase((p) => (p === "bulk_close" || p === "bulk" ? "done" : "check"));
  }, [setDemoPhase]);

  if (!step) return null;

  const pad = 10;
  const hole = rect
    ? {
        top: Math.max(0, rect.top - pad),
        left: Math.max(0, rect.left - pad),
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  const isCenter = step.kind === "center";
  const isDemo = step.kind === "demo";
  const isSpot = step.kind === "spotlight";

  const coachStep =
    isDemo && step.demo === "reports"
      ? { ...step, ...reportCoachCopy(demoPhase) }
      : isDemo && step.itemKind
      ? { ...step, ...demoCoachCopy(step.itemKind, demoPhase) }
      : step;

  const nextDisabled =
    isDemo &&
    !(
      demoPhase === "overview" ||
      demoPhase === "list" ||
      demoPhase === "done" ||
      (demoPhase === "eye" && Boolean(preview)) ||
      (demoPhase === "single" && Boolean(downloadInfo)) ||
      (demoPhase === "bulk" && Boolean(downloadInfo)) ||
      (step.demo === "reports" &&
        (demoPhase === "search" || demoPhase === "rows" || demoPhase === "done"))
    );

  const node = (
    <div className="ct-root" aria-live="polite">
      <div
        className="ct-block"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.preventDefault()}
        aria-hidden="true"
      />

      {isSpot && hole ? (
        <svg className="ct-mask" width={viewport.w} height={viewport.h}>
          <defs>
            <mask id="ct-cutout">
              <rect width={viewport.w} height={viewport.h} fill="white" />
              <rect
                x={hole.left}
                y={hole.top}
                width={hole.width}
                height={hole.height}
                rx="12"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width={viewport.w}
            height={viewport.h}
            fill="rgba(0,0,0,0.78)"
            mask="url(#ct-cutout)"
          />
          <rect
            x={hole.left}
            y={hole.top}
            width={hole.width}
            height={hole.height}
            rx="12"
            fill="none"
            stroke="rgba(255,255,255,0.95)"
            strokeWidth="2"
          />
        </svg>
      ) : (
        <div className="ct-veil" />
      )}

      {isSpot && step.arrow ? <Arrow rect={rect} placement={step.placement || "bottom"} /> : null}
      {isSpot && rect ? (
        <TipBubble rect={rect} text={step.body} placement={step.placement || "bottom"} />
      ) : null}

      <CoachBar
        step={coachStep}
        index={index}
        total={total}
        isFirst={isFirst}
        isLast={isLast}
        next={handleNext}
        back={back}
        skip={skip}
        nextDisabled={nextDisabled}
      />

      {isDemo ? (
        <div className={"ct-demo-layer" + (step.demo === "reports" ? " is-wide" : "")}>
          {step.demo === "forms" ? (
            <RegisterListDemo
              pageTitle="Select your branch, year, and month to open the related forms."
              pageHint="Statutory registers generated by your AccunityHR service provider."
              viewLabel="View forms"
              rowName="Form XII"
              itemKind="forms"
              phase={demoPhase}
              setPhase={setDemoPhase}
              onToast={showToast}
              onPreview={setPreview}
              onDownloadInfo={setDownloadInfo}
            />
          ) : null}
          {step.demo === "returns" ? (
            <RegisterListDemo
              pageTitle="Select your branch, year, and month to open the related returns."
              pageHint="PF and statutory returns managed with AccunityHR."
              viewLabel="View documents"
              rowName="PF Return"
              itemKind="returns"
              phase={demoPhase}
              setPhase={setDemoPhase}
              onToast={showToast}
              onPreview={setPreview}
              onDownloadInfo={setDownloadInfo}
            />
          ) : null}
          {step.demo === "docs" ? (
            <RegisterListDemo
              pageTitle="Select your branch, year, and month to open the related documents."
              pageHint="Licences and challans in one place."
              viewLabel="View documents"
              rowName="Trade Licence"
              itemKind="docs"
              phase={demoPhase}
              setPhase={setDemoPhase}
              onToast={showToast}
              onPreview={setPreview}
              onDownloadInfo={setDownloadInfo}
            />
          ) : null}
          {step.demo === "reports" ? (
            <ReportsDemo phase={demoPhase} setPhase={setDemoPhase} onToast={showToast} />
          ) : null}
        </div>
      ) : null}

      {isCenter ? (
        <div className="ct-center-card">
          <span className="ct-badge">ACCUNITYHR</span>
          <h2>{step.title}</h2>
          <p>{step.body}</p>
        </div>
      ) : null}

      <aside className={"ct-checklist" + (checklistOpen ? "" : " is-collapsed")}>
        <button type="button" className="ct-checklist-toggle" onClick={() => setChecklistOpen((v) => !v)}>
          <span>
            Progress · {checklistItems.filter((i) => i.status === "done").length}/{checklistItems.length}
          </span>
          <span>{checklistOpen ? "▾" : "▴"}</span>
        </button>
        {checklistOpen ? (
          <ul>
            {checklistItems.map((item) => (
              <li key={item.id} className={item.status === "current" ? "is-active" : ""}>
                <button type="button" onClick={() => jumpModule(item.id)}>
                  <span className={"ct-dot " + item.status}>
                    {item.status === "done" ? "✓" : item.status === "current" ? "●" : "○"}
                  </span>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <button type="button" className="ct-end" onClick={() => stopTour({ dismissed: true })}>
          End tour
        </button>
      </aside>

      <PreviewModal item={preview} onClose={handlePreviewClose} />
      <DownloadDialog item={downloadInfo} onClose={handleDownloadClose} />
      {toast ? <div className="ct-toast">{toast}</div> : null}
    </div>
  );

  return createPortal(node, document.body);
}

const emptyTour = {
  enabled: false,
  active: false,
  startTour: () => {},
  stopTour: () => {},
  next: () => {},
  back: () => {},
  skip: () => {},
  jumpModule: () => {},
  checklistItems: [],
  step: null,
  index: 0,
  total: 0,
  isFirst: true,
  isLast: true,
  checklistOpen: false,
  setChecklistOpen: () => {},
  demoPhase: "overview",
  setDemoPhase: () => {},
  completedModules: [],
};

export function ClientTourProvider({ children }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const [checklistOpen, setChecklistOpen] = useState(true);
  const [demoPhase, setDemoPhase] = useState("overview");

  const step = LIVE_TOUR_STEPS[index] || LIVE_TOUR_STEPS[0];
  const total = LIVE_TOUR_STEPS.length;
  const isFirst = index <= 0;
  const isLast = index >= total - 1;

  const markModule = useCallback((moduleId) => {
    if (!moduleId) return;
    setCompletedModules((prev) => (prev.includes(moduleId) ? prev : [...prev, moduleId]));
  }, []);

  const goToIndex = useCallback(
    (nextIndex) => {
      const safe = Math.max(0, Math.min(total - 1, nextIndex));
      const nextStep = LIVE_TOUR_STEPS[safe];
      setIndex(safe);
      setDemoPhase("overview");
      saveState({ index: safe, active: true });
      if (nextStep?.route) navigate(nextStep.route);
      if (nextStep?.openRegister) {
        window.dispatchEvent(new CustomEvent("client-tour-open-register"));
      }
      if (nextStep?.module) markModule(nextStep.module);
    },
    [navigate, total, markModule]
  );

  const startTour = useCallback(() => {
    setActive(true);
    setCompletedModules(["welcome"]);
    setChecklistOpen(true);
    setIndex(0);
    setDemoPhase("overview");
    saveState({ active: true, completed: false, dismissed: false, index: 0 });
    navigate("/dashboard");
  }, [navigate]);

  const stopTour = useCallback((opts = {}) => {
    setActive(false);
    saveState({
      active: false,
      dismissed: Boolean(opts.dismissed),
      completed: Boolean(opts.completed),
    });
  }, []);

  const next = useCallback(() => {
    const current = LIVE_TOUR_STEPS[index];
    if (current?.kind === "demo") {
      const reportsReady = current.demo === "reports" && (demoPhase === "rows" || demoPhase === "done");
      if (demoPhase !== "done" && !reportsReady) return;
    }
    if (isLast) {
      markModule("done");
      stopTour({ completed: true });
      navigate("/user-guide");
      return;
    }
    goToIndex(index + 1);
  }, [isLast, index, goToIndex, stopTour, navigate, markModule, demoPhase]);

  const back = useCallback(() => {
    if (!isFirst) goToIndex(index - 1);
  }, [isFirst, index, goToIndex]);

  const skip = useCallback(() => stopTour({ dismissed: true }), [stopTour]);

  const jumpModule = useCallback(
    (moduleId) => {
      const stepIndex = LIVE_TOUR_STEPS.findIndex((s) => s.module === moduleId);
      if (stepIndex < 0) return;
      setActive(true);
      goToIndex(stepIndex);
    },
    [goToIndex]
  );

  const checklistItems = useMemo(() => {
    const current = step?.module;
    return TOUR_CHECKLIST.map((m) => {
      let status = "todo";
      if (completedModules.includes(m.id)) status = "done";
      if (active && current === m.id) status = "current";
      return { ...m, status };
    });
  }, [completedModules, step, active]);

  const value = {
    enabled: true,
    active,
    step,
    index,
    total,
    isFirst,
    isLast,
    checklistItems,
    checklistOpen,
    setChecklistOpen,
    startTour,
    stopTour,
    next,
    back,
    skip,
    jumpModule,
    demoPhase,
    setDemoPhase,
    completedModules,
  };

  return (
    <ClientTourContext.Provider value={value}>
      {children}
      {active ? <ClientTourOverlay /> : null}
    </ClientTourContext.Provider>
  );
}

export function useClientTour() {
  return useContext(ClientTourContext) || emptyTour;
}

export default ClientTourProvider;
